import { Tab, Tabs } from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Typography } from '~/components'
import UserSettingsTab from './UserSettingsTab'
import { AccountType } from '~/constants/settings'
import { useBillingContext } from '~/contexts/BillingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import ApiKeysSettingsTab from './ApiKeysSettingsTab'
import RunnerSettingsTab from './RunnerSettingsTab'
import { bridge } from '~/services/bridge'
import BookmarksSettingsTab from './BookmarksSettingsTab'
import DownloadInstructionsSettingsTab from './DownloadInstructionsSettingsTab'
import IntegrationsSettingsTab, { useHasVisibleIntegrations } from './IntegrationsSettingsTab'
import { RepositoriesSettingsTab } from './RepositoriesSettingsTab'
import ScheduledTasksSettingsTab from './ScheduledTasksSettingsTab'
import WorkspaceSettingsTab from './WorkspaceSettingsTab'

export enum SettingsTab {
  General = 'general',
  User = 'user',
  Workspace = 'workspace',
  Bookmarks = 'bookmarks',
  Repositories = 'repositories',
  Integrations = 'integrations',
  Indexers = 'indexers',
  LocalRepositories = 'local-repositories',
  Billing = 'billing',
  Runner = 'runner',
  ApiKeys = 'api-keys',
  ScheduledTasks = 'scheduled-tasks',
  Download = 'download'
}

const SETTINGS_TAB_WIDTH: Record<string, string> = {
  User: 'w-[52px]',
  Workspace: 'w-[112px]',
  Bookmarks: 'w-[108px]',
  Repositories: 'w-[124px]',
  Integrations: 'w-[112px]',
  Runner: 'w-[76px]',
  'API Keys': 'w-[84px]',
  'Scheduled Tasks': 'w-[148px]',
  Plugin: 'w-[76px]'
}

function SettingsTabTitle({ label }: { label: string }) {
  return (
    <span
      className={`relative inline-flex justify-center px-3 !text-sm leading-none sm:px-4 sm:!text-base ${
        SETTINGS_TAB_WIDTH[label] ?? 'w-auto'
      }`}
      data-testid={`settings-tab-title-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className='absolute inset-0 flex items-center justify-center'>{label}</span>
    </span>
  )
}

function SettingsPage() {
  const { currentWorkspace } = useWorkspaceContext()
  const [tabSelected, setTabSelected] = useState<SettingsTab>(SettingsTab.User)
  const { hasLicense } = useBillingContext()
  const hasVisibleIntegrations = useHasVisibleIntegrations()
  const isPlugin = bridge.isInPlugin()

  // Get workspace admin status
  const isAdmin = currentWorkspace?.is_admin ?? false

  // URL-based routing
  const [searchParams, setSearchParams] = useSearchParams()

  // Check if user has any type of enterprise license
  const isEnterpriseUser =
    hasLicense(AccountType.ENTERPRISE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ASSESS) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ACCELERATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_REFACTOR)

  const isTeamUser = hasLicense(AccountType.TEAM)

  // URL-based tab handling (same pattern as index.tsx)
  useEffect(() => {
    const urlTab = searchParams.get('tab')
    if (urlTab && Object.values(SettingsTab).includes(urlTab as SettingsTab)) {
      setTabSelected(urlTab as SettingsTab)
    } else {
      setTabSelected(SettingsTab.User) // Default tab
    }
  }, [searchParams, setSearchParams])

  // Handle tab changes - update URL
  const handleTabChange = (newTab: SettingsTab) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('tab', newTab)
    setSearchParams(newSearchParams)
  }

  return (
    <div
      className='w-full min-h-full mx-auto bg-white px-4 pb-[60px] sm:px-12 sm:pb-[200px] md:px-[100px] xl:px-[120px]'
      aria-label='Settings page'
    >
      <div
        className='flex w-full flex-col mx-auto max-w-full pt-6 sm:max-w-[919px] sm:pt-12'
        data-testid='settings-content-shell'
      >
        <Typography
          fontFamily='geist'
          fontSize='headlineSm'
          fontWeight='semibold'
          className='px-4 py-3 select-none text-tokens-neutral-text-primary'
        >
          Settings
        </Typography>
        <Tabs
          variant='underlined'
          aria-label='Settings Tab'
          selectedKey={tabSelected}
          onSelectionChange={key => handleTabChange(key as SettingsTab)}
          className='p-0'
          classNames={{
            base: 'sticky top-0 z-20 bg-white pt-4 overflow-x-auto sm:overflow-x-visible',
            tabList: 'p-0 gap-0 flex-nowrap sm:flex-wrap',
            tab: 'justify-start p-0 data-[selected=true]:label-lg-bold label-lg-normal w-fit h-[41px] select-none',
            cursor: 'hidden',
            panel: 'hidden'
          }}
        >
          <Tab
            key={SettingsTab.User}
            title={<SettingsTabTitle label='User' />}
            aria-label='Setting User tab'
          />
          <Tab key={SettingsTab.Workspace} title={<SettingsTabTitle label='Workspace' />} />
          <Tab
            key={SettingsTab.Bookmarks}
            title={<SettingsTabTitle label='Bookmarks' />}
            aria-label='Setting Bookmarks tab'
          />
          {isAdmin ? (
            <Tab
              aria-label='repositories-tab'
              key={SettingsTab.Repositories}
              title={<SettingsTabTitle label='Repositories' />}
            />
          ) : null}
          {isAdmin && hasVisibleIntegrations ? (
            <Tab
              aria-label='integrations-tab'
              key={SettingsTab.Integrations}
              title={<SettingsTabTitle label='Integrations' />}
            />
          ) : null}
          {isEnterpriseUser ? (
            <Tab key={SettingsTab.Runner} title={<SettingsTabTitle label='Runner' />} />
          ) : null}
          {isAdmin && (isEnterpriseUser || isTeamUser) ? (
            <Tab key={SettingsTab.ApiKeys} title={<SettingsTabTitle label='API Keys' />} />
          ) : null}
          <Tab
            key={SettingsTab.ScheduledTasks}
            title={<SettingsTabTitle label='Scheduled Tasks' />}
            aria-label='Setting Scheduled Tasks tab'
          />
          {!isPlugin && (
            <Tab key={SettingsTab.Download} title={<SettingsTabTitle label='Plugin' />} />
          )}
        </Tabs>
        <div className='pt-4' data-testid='settings-tabs-content-shell'>
          <div
            className='w-full border-t border-tokens-neutral-border-1'
            aria-hidden='true'
            data-testid='settings-tabs-divider'
          />
          <div className='pt-8' data-testid='settings-tab-content'>
            {tabSelected === SettingsTab.User && <UserSettingsTab />}
            {tabSelected === SettingsTab.Workspace && <WorkspaceSettingsTab />}
            {tabSelected === SettingsTab.Bookmarks && <BookmarksSettingsTab />}
            {tabSelected === SettingsTab.Repositories && isAdmin && <RepositoriesSettingsTab />}
            {tabSelected === SettingsTab.Integrations && isAdmin && hasVisibleIntegrations && (
              <IntegrationsSettingsTab />
            )}
            {tabSelected === SettingsTab.Runner && isEnterpriseUser && <RunnerSettingsTab />}
            {tabSelected === SettingsTab.ApiKeys && isAdmin && (isEnterpriseUser || isTeamUser) && (
              <ApiKeysSettingsTab />
            )}
            {tabSelected === SettingsTab.ScheduledTasks && <ScheduledTasksSettingsTab />}
            {tabSelected === SettingsTab.Download && <DownloadInstructionsSettingsTab />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
